/**
 * NotificationBell — Central de Sugestões proativas no header do tenant.
 *
 * Sino distinto do StatusReminderBell (que só cuida do "marcar status" imediato).
 * Este agrega TODAS as sugestões do sistema: estoque, clientes em risco,
 * agenda ociosa, financeiro pendente, WhatsApp com problemas.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Package, Users, Calendar, DollarSign, MessageSquare,
  Check, Clock, X, ArrowRight,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNotifications } from "@/contexts/NotificationsContext";

const CATEGORY_META = {
  estoque: { label: "Estoque", icon: Package, color: "text-emerald-700" },
  clientes: { label: "Clientes", icon: Users, color: "text-blue-700" },
  agenda: { label: "Agenda", icon: Calendar, color: "text-amber-700" },
  financeiro: { label: "Financeiro", icon: DollarSign, color: "text-purple-700" },
  whatsapp: { label: "WhatsApp", icon: MessageSquare, color: "text-green-700" },
};

const PRIORITY_STYLE = {
  high: "bg-red-500/10 text-red-700 border-red-500/30",
  medium: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  low: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
};
const PRIORITY_LABEL = { high: "Alta", medium: "Média", low: "Baixa" };

export default function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const { items, total, byCategory, countsByPriority, resolve, snooze, dismiss } = useNotifications();

  const highPri = countsByPriority.high || 0;

  const handleAction = async (action, item) => {
    setBusy(item.id);
    try {
      if (action === "resolve") { await resolve(item.id); toast.success("Marcado como resolvido"); }
      else if (action === "snooze") { await snooze(item.id, 7); toast.success("Adiado por 7 dias"); }
      else if (action === "dismiss") { await dismiss(item.id); toast("Notificação dispensada"); }
    } catch (e) {
      toast.error("Não foi possível atualizar a notificação");
    } finally { setBusy(null); }
  };

  const navigateTo = (url) => {
    if (!url) return;
    setOpen(false);
    nav(url);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notifications-bell"
          aria-label="Sugestões do sistema"
          className="relative w-9 h-9 rounded-md grid place-items-center hover:bg-muted transition-colors"
        >
          <Sparkles className={`w-4 h-4 ${highPri > 0 ? "text-red-500" : total > 0 ? "text-amber-600" : ""}`} strokeWidth={1.8} />
          {total > 0 && (
            <span
              data-testid="notifications-bell-badge"
              className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold grid place-items-center text-white ${
                highPri > 0 ? "bg-red-500" : "bg-amber-500"
              }`}
            >
              {total}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="bottom"
        className="w-[420px] p-0 max-h-[75vh] overflow-y-auto"
        data-testid="notifications-popover"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-display font-semibold text-sm flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Sugestões do sistema
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {total === 0 ? "Tudo em dia. Sem pontos de atenção agora." :
                `${total} ponto(s) que requer(em) sua atenção`}
            </div>
          </div>
        </div>

        {total === 0 ? (
          <div className="p-8 text-center text-xs text-muted-foreground">
            🎉 Você está no controle. Bora crescer!
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((n) => {
              const meta = CATEGORY_META[n.category] || CATEGORY_META.estoque;
              const Icon = meta.icon;
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className="p-3 space-y-2 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-8 h-8 rounded-md border border-border grid place-items-center shrink-0 ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <div className="text-sm font-medium leading-tight flex-1 min-w-0">{n.title}</div>
                        <Badge variant="outline" className={`${PRIORITY_STYLE[n.priority]} text-[10px] shrink-0`}>
                          {PRIORITY_LABEL[n.priority] || n.priority}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-3">{n.message}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-wrap pl-10">
                    {(n.quick_actions || []).slice(0, 2).map((qa) => (
                      <Button
                        key={qa.key}
                        size="sm" variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => navigateTo(qa.url)}
                        data-testid={`notif-action-${qa.key}-${n.id}`}
                      >
                        {qa.label} <ArrowRight className="w-3 h-3" />
                      </Button>
                    ))}
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 text-xs gap-1"
                      onClick={() => handleAction("snooze", n)}
                      disabled={busy === n.id}
                      data-testid={`notif-snooze-${n.id}`}
                      title="Adiar por 7 dias"
                    >
                      <Clock className="w-3 h-3" /> 7d
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 text-xs gap-1 text-emerald-700 hover:text-emerald-700 hover:bg-emerald-500/10"
                      onClick={() => handleAction("resolve", n)}
                      disabled={busy === n.id}
                      data-testid={`notif-resolve-${n.id}`}
                    >
                      <Check className="w-3 h-3" /> Resolver
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-red-700 hover:bg-red-500/10"
                      onClick={() => handleAction("dismiss", n)}
                      disabled={busy === n.id}
                      data-testid={`notif-dismiss-${n.id}`}
                      title="Dispensar"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {total > 0 && (
          <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground bg-muted/40 flex items-center justify-between">
            <span>
              {Object.entries(byCategory).map(([k, v]) => (
                <span key={k} className="mr-3">{CATEGORY_META[k]?.label || k}: <b className="text-foreground">{v.length}</b></span>
              ))}
            </span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
